/**
 * Auth Thunks
 *
 * Login is handled by the MMT global header ($header.js).
 * These thunks handle auth status checking and logout only.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  isLoggedIn,
  getCookieDomain,
  AUTH_COOKIE_NAME,
  UUID_COOKIE_NAME,
} from '@/shared/utils/auth';
import { removeCookie } from '@/shared/utils/cookies';
import { clearSessionId } from '@/shared/utils';

// ============================================
// Thunks
// ============================================

/**
 * Check authentication status by inspecting cookies.
 * Called on app init and whenever the MmtHeader detects a cookie change.
 */
export const checkAuthStatusThunk = createAsyncThunk<{ isLoggedIn: boolean }, void>(
  'auth/checkStatus',
  async () => {
    return { isLoggedIn: isLoggedIn() };
  },
);

/**
 * Logout user
 * Removes auth cookies and resets state.
 */
export const logoutThunk = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const cookieDomain = getCookieDomain();

      // Remove cookies with both secure and non-secure variants
      // because MMT's server may set cookies without Secure flag
      for (const secure of [true, false]) {
        removeCookie(AUTH_COOKIE_NAME, {
          path: '/',
          domain: cookieDomain,
          secure,
          sameSite: 'Lax',
        });
        removeCookie(UUID_COOKIE_NAME, {
          path: '/',
          domain: cookieDomain,
          secure,
          sameSite: 'Strict',
        });
      }

      // Also try without domain (browser default) in case cookies
      // were set without an explicit domain attribute
      removeCookie(AUTH_COOKIE_NAME, { path: '/' });
      removeCookie(UUID_COOKIE_NAME, { path: '/' });

      clearSessionId();

      return undefined;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Logout failed');
    }
  },
);
